import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import {
  CHART_LEGEND_CONFIG,
  CHART_TOOLTIP_CONFIG,
  TASKS_COLORS,
} from '@/src/lib/chartConfig.js';
import { WidgetWrapper } from '@/src/components/projects/ProjectDashboard/WidgetWrapper.jsx';
import { WidgetInner } from '@/src/components/projects/ProjectDashboard/WidgetInner.jsx';
import { WidgetCard } from '@/src/components/projects/ProjectDashboard/WidgetCard.jsx';
import SentimentDot from '@/src/components/SentimentDot.jsx';
import { changeImpactTasksWidgetContent } from '@/src/components/EmptyStates.jsx';
import Empty from '@/src/components/Empty.jsx';
import { buildPath } from '@/src/components/Link.jsx';
import { CHANGE_IMPACTS_INDEX } from '@/src/constants/routes.js';

export const CITasksWidget = ({ data }) => {
  const navigate = useNavigate();
  const { slug, projectId } = useParams();

  const handleNavigateToChangeImpacts = () => {
    const path = buildPath(CHANGE_IMPACTS_INDEX, { slug, projectId });
    navigate(path);
  };

  const { notStarted, completed, inProgress, total } = data;
  const hasData = total > 0;

  const chartData = {
    labels: ['Not Started', 'In Progress', 'Completed'],
    datasets: [
      {
        data: [notStarted, inProgress, completed],
        backgroundColor: [
          TASKS_COLORS.notStarted,
          TASKS_COLORS.inProgress,
          TASKS_COLORS.completed,
        ],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '50%',
    plugins: {
      legend: CHART_LEGEND_CONFIG,
      tooltip: {
        ...CHART_TOOLTIP_CONFIG,
        callbacks: {
          ...CHART_TOOLTIP_CONFIG.callbacks,
          title: () => null,
          label: function (context) {
            return `${context.label}: ${context.parsed}`;
          },
        },
      },
    },
  };

  return (
    <WidgetWrapper className="ci-tasks-widget">
      <div className="flex align-center m-b-12">
        <h2>Change Impact Tasks</h2>
        <strong className="header-item bordered component-bg-white text-primary">
          <span className="text-muted">Total: </span>
          <span>{total}</span>
        </strong>
      </div>
      {hasData ? (
        <WidgetInner height={365} className="flex flex-align-start">
          <div className="left">
            <WidgetCard
              sentiment="not-started"
              className="p-x-20 p-y-16 m-b-16"
            >
              <div className="flex align-center flex-justify">
                <div className="flex align-center">
                  <SentimentDot sentiment="not-started" />
                  <div className="font-size-16 font-semibold">Not Started</div>
                </div>
                <div className="font-size-16 font-semibold">{notStarted}</div>
              </div>
            </WidgetCard>
            <WidgetCard
              sentiment="in-progress"
              className="p-x-20 p-y-16 m-b-16"
            >
              <div className="flex align-center flex-justify">
                <div className="flex align-center">
                  <SentimentDot sentiment="in-progress" />
                  <div className="font-size-16 font-semibold">In Progress</div>
                </div>
                <div className="font-size-16 font-semibold">{inProgress}</div>
              </div>
            </WidgetCard>
            <WidgetCard sentiment="completed" className="p-x-20 p-y-16 m-b-16">
              <div className="flex align-center flex-justify">
                <div className="flex align-center">
                  <SentimentDot sentiment="completed" />
                  <div className="font-size-16 font-semibold">Completed</div>
                </div>
                <div className="font-size-16 font-semibold">{completed}</div>
              </div>
            </WidgetCard>
          </div>
          <div className="right">
            <div style={{ height: '300px', width: '100%' }}>
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </div>
        </WidgetInner>
      ) : (
        <WidgetInner height={365} className="flex flex-col">
          <Empty
            simplified
            content={changeImpactTasksWidgetContent}
            action={handleNavigateToChangeImpacts}
          />
        </WidgetInner>
      )}
    </WidgetWrapper>
  );
};
